import { useState } from "react";
import { Table, Button, Form, Image, Row, Col, Card, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import Message from "../../Components/Message";
import Loader from "../../Components/Loader";
import BackButton from "../../Components/BackButton";
import {
  useAdminListVehicleMakesQuery,
  useAdminCreateMakeMutation,
  useAdminUpdateMakeMutation,
  useAdminDeleteMakeMutation,
  useUploadImageMutation,
  useDeleteImageMutation,
} from "../../slices/productsApiSlice";

export default function MakeAdminScreen() {
  const {
    data: items = [],
    isLoading,
    error,
    refetch,
  } = useAdminListVehicleMakesQuery();

  const [createMake, { isLoading: creating }] = useAdminCreateMakeMutation();
  const [updateMake, { isLoading: updating }] = useAdminUpdateMakeMutation();
  const [deleteMake, { isLoading: deleting }] = useAdminDeleteMakeMutation();
  const [uploadImage, { isLoading: uploading }] = useUploadImageMutation();
  const [deleteImage] = useDeleteImageMutation();

  const [form, setForm] = useState({
    make: "",
    logo: "",
    sort: 0,
    active: true,
  });

  const busy = creating || updating || deleting || uploading;

  const sortedItems = [...items].sort((a, b) => {
    const as = Number.isFinite(a?.sort) ? a.sort : Number.MAX_SAFE_INTEGER;
    const bs = Number.isFinite(b?.sort) ? b.sort : Number.MAX_SAFE_INTEGER;
    if (as !== bs) return as - bs;
    return (a?.make || "").localeCompare(b?.make || "");
  });

  // --- HELPER: Upload to S3 and return URL ---
  const uploadFileHandler = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    const res = await uploadImage(formData).unwrap();
    return res.imageUrl;
  };

  // --- CREATE ---
  const onCreate = async () => {
    if (!form.make.trim()) return;
    try {
      await createMake({
        make: form.make.trim(),
        logo: form.logo.trim(),
        sort: Number(form.sort) || 0,
        active: !!form.active,
      }).unwrap();

      setForm({ make: "", logo: "", sort: 0, active: true });
      toast.success("Make added");
      await refetch();
    } catch (e) {
      toast.error(e?.data?.message || e?.error || "Create failed");
    }
  };

  // --- EDIT UPLOAD HANDLER ---
  const onEditUpload = async (e, makeItem) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.warn("Please choose a PNG, JPG, or WebP image");
      return;
    }

    try {
      const newUrl = await uploadFileHandler(file);
      await updateMake({ id: makeItem._id, logo: newUrl }).unwrap();

      if (makeItem.logo) {
        await deleteImage({ image: makeItem.logo }).unwrap();
      }

      toast.success("Logo updated");
      await refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const handleDelete = async (make) => {
    if (!window.confirm(`Delete ${make.make} permanently?`)) return;
    try {
      if (make.logo) {
        await deleteImage({ image: make.logo }).unwrap();
      }
      await deleteMake(make._id).unwrap();
      toast.success("Make deleted");
      await refetch();
    } catch (e) {
      toast.error(e?.data?.message || e?.error || "Delete failed");
    }
  };

  const handleUpdateField = async (id, field, value) => {
    try {
      await updateMake({ id, [field]: value }).unwrap();
      await refetch();
    } catch (e) {
      toast.error(e?.data?.message || "Update failed");
    }
  };

  return (
    <>
      <BackButton />
      <h1>Vehicle Makes</h1>

      {/* --- CREATE CARD --- */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label>Make</Form.Label>
              <Form.Control
                value={form.make}
                onChange={(e) => setForm({ ...form, make: e.target.value })}
                placeholder="e.g., Toyota"
                disabled={busy}
              />
            </Col>

            <Col md={4}>
              <Form.Label>Logo</Form.Label>
              <Form.Control
                value={form.logo}
                onChange={(e) => setForm({ ...form, logo: e.target.value })}
                placeholder="Image URL..."
                disabled={busy}
              />
            </Col>

            <Col md={2}>
              <Form.Label>Sort</Form.Label>
              <Form.Control
                type="number"
                value={form.sort}
                onChange={(e) =>
                  setForm({ ...form, sort: Number(e.target.value) })
                }
                disabled={busy}
              />
            </Col>

            <Col md={1}>
              <Form.Label>Active</Form.Label>
              <div>
                <Form.Check
                  type="switch"
                  checked={form.active}
                  onChange={(e) =>
                    setForm({ ...form, active: e.target.checked })
                  }
                  disabled={busy}
                />
              </div>
            </Col>

            <Col md={1} className="d-flex">
              <Button onClick={onCreate} disabled={!form.make.trim() || busy}>
                Add
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* --- LIST TABLE --- */}
      {isLoading && <Loader />}
      {error && (
        <Message variant="danger">
          {error?.data?.message || error.error}
        </Message>
      )}

      {!isLoading && !error && (
        <Table striped bordered hover responsive className="table-sm align-middle">
          <thead>
            <tr>
              <th style={{ width: 96 }}>Logo</th>
              <th>Make</th>
              <th className="text-center">Active</th>
              <th style={{ width: 110 }}>Sort</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((it) => (
              <tr key={it._id} style={{ opacity: busy ? 0.6 : 1 }}>
                <td>
                  {it.logo ? (
                    <Image
                      src={it.logo}
                      alt={it.make}
                      style={{ maxHeight: 36 }}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : null}
                </td>

                <td>
                  <div className="d-flex align-items-center gap-2">
                    {!it.active && <Badge bg="secondary">Inactive</Badge>}
                  </div>
                  <Form.Control
                    defaultValue={it.make}
                    disabled={busy}
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val && val !== it.make)
                        handleUpdateField(it._id, "make", val);
                    }}
                  />
                </td>

                <td className="text-center">
                  <Form.Check
                    type="switch"
                    defaultChecked={!!it.active}
                    disabled={busy}
                    onChange={(e) =>
                      handleUpdateField(it._id, "active", e.target.checked)
                    }
                  />
                </td>

                <td>
                  <Form.Control
                    type="number"
                    defaultValue={it.sort || 0}
                    disabled={busy}
                    onBlur={(e) => {
                      const val = Number(e.target.value);
                      const next = Number.isFinite(val) ? val : 0;
                      if (next !== (it.sort || 0))
                        handleUpdateField(it._id, "sort", next);
                    }}
                  />
                </td>

                <td className="text-end">
                  <div className="d-flex gap-2 justify-content-end">
                    <Form.Control
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      disabled={busy}
                      onChange={(e) => onEditUpload(e, it)}
                    />

                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busy}
                      onClick={() => handleDelete(it)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}
